import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, take } from 'rxjs/operators';
import { showAnimation } from 'src/animations';
import { ItemAssistDialogOutput, ItemAssistentDialogComponent } from 'src/app/lib/issue/components/dialog/item-assistent-dialog/item-assistent-dialog.component';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { CONCURRENCY_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { Contract, ContractItem } from '../../../classes';
import { CONTRACT_PROVIDER_SERVICE } from '../../../interfaces';
import { ContractService } from '../../../services/contract.service';
import { ContractDetailViewComponentService } from './contract-detail-view-component-service';

@Component({
  selector: 'app-contract-detail-view',
  templateUrl: './contract-detail-view.component.html',
  styleUrls: ['./contract-detail-view.component.css'],
  animations: [showAnimation],
  providers: [
    { provide: CONTRACT_PROVIDER_SERVICE, useClass: ContractDetailViewComponentService },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: CONTRACT_PROVIDER_SERVICE },
    SidenavService
  ]
})
export class ContractDetailViewComponent implements OnInit {

  disabled: boolean = false;

  private _sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'activate': {
      'icon': () => 'upcoming',
      'title': () => 'Aktiv',
      'onClick': () => of((this.formGroup.controls['contract'] as FormGroup).controls['contractstate'].setValue(1)),
      'visible': () => (this.formGroup.controls['contract'] as FormGroup).controls['contractstate'].value === 2,
      'appearance': () => 'ok'
    } as SidenavOption,
    'showCustomer': {
      'icon': () => 'domain',
      'title': () => 'Kundendetails',
      'onClick': () => of(this.navigateToCustomer()),
      'visible': () => typeof (this.formGroup.controls['contract'] as FormGroup).controls['contractcustomer'].value === 'number',
      'appearance': () => 'primary'
    } as SidenavOption,
    'watched_status': {
      'icon': () => this.formGroup.controls['isWatched'].value ? 'visibility' : 'visibility_off',
      'title': () => this.formGroup.controls['isWatched'].value ? 'Beobachtet' : 'Nicht beob.',
      'onClick': () => this.toggleWatchedStatus(),
      'visible': () => true,
      'appearance': () => 'dark'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteContract(),
      'visible': () => !this.disabled && this.rightsMap.delete_contracts,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  reset: boolean = false;

  itemsRefreshing: boolean = false;

  formGroup: FormGroup;
  
  rightsMap = {
    "delete_contracts": false
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(CONTRACT_PROVIDER_SERVICE) public contractDetailViewComponentService: ContractDetailViewComponentService,
    private _formBuilder: FormBuilder,
    private _sidenavService: SidenavService,
    private _matDialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _dialog: MatDialog,
    private _sessionService: SessionService,
    private _contractService: ContractService
  ) { }

  addArticle() {
    this.contractDetailViewComponentService.contractId$.pipe(take(1)).subscribe((identifier) => {
      let article = new ContractItem();
      article.itemcontractid = identifier;
      article.itemposition = Math.max(...(this.itemsControl.value as ContractItem[]).map(x => x.itemposition), 0) + 1;
      this.itemsControl.push(this._formBuilder.group(article));
    })
  }

  afterStoring() {
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
    this.contractDetailViewComponentService.refreshCurrentContract();
    this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
      duration: 2000
    });
  }

  deleteContract() {
    let subject = new Subject();
    this.contractDetailViewComponentService.contract$.pipe(take(1)).subscribe((contract: Contract) => {
      var number = contract.contractnumber? contract.contractnumber.trim(): Math.floor(1000 + Math.random() * 9000).toFixed(0);
      this._dialog.open(DoubleCheckDialogComponent, {
        autoFocus: false,
        panelClass: 'no-padding-dialog-container',
        data: {
          mode: 'numeric',
          title: 'Löschen bestätigen',
          text: `Bitte geben Sie die Vertragsnummer ${number} ein, um den Vertrag dauerhaft zu löschen`,
          doubleCheckValue: number
        } as DoubleCheckDialogComponentInputData
      }).afterClosed().subscribe((result) => {
        if (result) {
          this.contractDetailViewComponentService.deleteCurrentContract().subscribe(subject);
        } else subject.complete();
      });
    });
    return subject.asObservable();
  }

  disable() {
    let pristine = this.formGroup.pristine;
    this.formGroup.disable({ emitEvent: false });
    this.disabled = true;
    if (pristine) this.formGroup.markAsPristine();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.contractDetailViewComponentService.refreshCurrentContract().pipe(finalize(() => this.reset = false));
  }

  enable() {
    let pristine = this.formGroup.pristine;
    this.formGroup.enable({ emitEvent: false });
    this.disabled = false;
    if (pristine) this.formGroup.markAsPristine();
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  markContractAsInactive() {
    let contractNumber = (this.formGroup.controls['contract'] as FormGroup).controls['contractnumber'].value;
    this._matDialog.open(DoubleCheckDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      data: {
        'icon': 'block',
        'title': 'Vertrag auf inaktiv setzen',
        'text': `Sind Sie sicher, dass Sie diesen Vertrag als inaktiv markieren möchten? Der aktuelle Stand wird direkt gespeichert. Geben Sie bitte die Vertragsnummer ${contractNumber} ein um fortzufahren.`,
        'doubleCheckValue': contractNumber,
        'mode': 'numeric'
      } as DoubleCheckDialogComponentInputData
    }).afterClosed().subscribe((result) => {
      if(result) this.saveChanges();
      else {
        this.reset = true;
        this.enable();
        this.contractDetailViewComponentService.refreshCurrentContract().subscribe(() => this.reset = false);
      }
    });
  }

  navigateToCustomer = () => this._router.navigate(['/customer-details', (this.formGroup.controls['contract'] as FormGroup).controls['contractcustomer'].value]);

  ngOnInit(): void {

    this.formGroup = this._formBuilder.group({
      contract: this._formBuilder.group(new Contract()),
      contractItems: this._formBuilder.array([]),
      isWatched: this._formBuilder.control(false)
    });

    this._sidenavService.setOptions(Object.values(this._sidenavOptions));

    this._subscriptions.push(...[
      this.contractDetailViewComponentService.contract$.pipe(filter(x => this.formGroup.pristine || this.reset)).subscribe((contract: Contract) => this.setContract(contract)),
      (this.formGroup.controls['contract'] as FormGroup).controls['contractstate'].valueChanges.pipe(distinctUntilChanged(), switchMap((status) => combineLatest([of(status), this.contractDetailViewComponentService.contract$]))).subscribe(([status, contract]) => {
        if (status === 2) {
          this.disable();
          if (status !== contract.contractstate) this.markContractAsInactive();
        } else this.enable();
      }),
      combineLatest([this.itemsControl.valueChanges, this.contractDetailViewComponentService.contract$]).pipe(debounceTime(100)).subscribe(([value, contract]) => {
        if (this.formGroup.pristine && ((Object.values(this.itemsControl.controls).some(x => x.dirty)) || this.itemsControl.value.length !== contract.contractitem.length)) this.formGroup.markAsDirty();
      })
    ]);

    this.initRights();

  }

  openItemAssist = () => {
    this._matDialog.open(ItemAssistentDialogComponent, {
      panelClass: 'no-padding-dialog-container'
    }).afterClosed().pipe(
      switchMap((result: ItemAssistDialogOutput) => combineLatest([of(result), this.contractDetailViewComponentService.contractId$]))
    ).subscribe(([result, contractId]) => {
      let max = Math.max(...this.itemsControl.value.map(x => x.itemposition), 0);
      for (let item of result.items) {
        max++;
        this.itemsControl.push(this._formBuilder.group({
          itemid: null,
          itembuildindate: item.itembuildindate,
          itemcanceldate: item.itemcanceldate,
          itemcount: 1,
          itemdesc: item.itemdesc,
          itemfaktdate: item.itemfaktdate,
          iteminfo: null,
          itemposition: max,
          itemproduct: item.itemproduct,
          itempurchasecost: item.itempurchasecost,
          itemrentalcost: item.itemrentalcost,
          itemsetupcost: item.itemsetupcost,
          itemcontractid: contractId,
          itemname: item.itemservice,
          itemlocation: null
        } as ContractItem));
      }
    });
  };

  resetContractItems(contract: Contract) {
    this.itemsRefreshing = true;
    timer(100).subscribe(() => {
      this.itemsControl.clear();
      contract.contractitem.forEach(x => {
        let group = this._formBuilder.group(x);
        this.itemsControl.push(group);
        if (this.disabled) group.disable();
      });
      this.itemsRefreshing = false;
    });
  }

  saveChanges(): Observable<any> {
    let subject = new Subject();

    combineLatest([
      this._matDialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.contractDetailViewComponentService.contract$
    ])
      .pipe(take(1))
      .subscribe(([output, contract]) => {
        if (output.abort) {
          subject.complete();
          return;
        }

        let formItemIds = (this.itemsControl.getRawValue() as ContractItem[]).map(x => x.itemid);
        this.contractDetailViewComponentService
          .saveChanges(
            (this.formGroup.controls['contract'] as FormGroup).getRawValue(),
            output.note,
            Object.values(this.itemsControl.controls).filter(x => x.dirty || x.value.itemid === null).map(x => (x as FormGroup).getRawValue()),
            contract.contractitem.map(x => x.itemid).filter(x => formItemIds.indexOf(x) === -1)
          )
          .pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);
      });

    return subject.asObservable();
  }

  setContract(contract: Contract) {
    this.formGroup.controls['contract'].patchValue(contract);
    this.resetContractItems(contract);
    this.formGroup.markAsPristine();
    timer(100).pipe(switchMap(() => this._contractService.contractIsWatched(contract.contractid))).subscribe((result: boolean) => this.formGroup.controls['isWatched'].setValue(result));
    this._contractService.saveHistory(contract.contractid);
  }

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  toggleWatchedStatus() {
    let value = this.formGroup.controls['isWatched'].value ? true : false;
    this.contractDetailViewComponentService.contractId$.pipe(switchMap((contractId: number) => this._contractService.setContractWatchedStatus(contractId, !value)))
      .subscribe(() => this.formGroup.controls['isWatched'].setValue(!value));

    return of(null);
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent){
    if(event.ctrlKey && event.key === 's'){
      event.preventDefault();
      event.stopPropagation();
      if(this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  get itemsControl(): FormArray {
    return this.formGroup.controls['contractItems'] as FormArray;
  }

}
